/**
 * V2 query layer — caching and request dedup over the raw REST gateway.
 *
 * V1 panels re-fetch on every open; this layer gives every query a key,
 * a TTL, and in-flight sharing so concurrent callers don't duplicate
 * network requests. Data access still goes through the committed
 * rawQuery REST design (see docs/AUTH-AND-SDK.md) — this only wraps it.
 */

const cache = new Map(); // key → { data, expires }
const inflight = new Map(); // key → Promise

/**
 * Run a fetcher with caching + dedup.
 * @param {string} key - Cache key (include all query inputs in it)
 * @param {() => Promise<any>} fetcher - Returns the value to cache
 * @param {object} opts
 * @param {number} opts.ttl - Cache lifetime in ms (default 60s)
 */
export async function cached(key, fetcher, { ttl = 60_000 } = {}) {
  const hit = cache.get(key);
  if (hit && hit.expires > Date.now()) return hit.data;

  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    try {
      const data = await fetcher();
      cache.set(key, { data, expires: Date.now() + ttl });
      return data;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

/** Drop cache entries whose key starts with the given prefix ('' = all). */
export function invalidate(prefix = '') {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}
